import useIsNarrow from "../hooks/useIsNarrow";

/**
 * "How is this calculated?" for the IRR report.
 *
 * Written against what irrCalculator.js actually does, not IRR in general — the numbers in
 * the worked example below are the real output of generateCashFlowProjection for those
 * inputs, so if the model changes this text is wrong and should be changed with it.
 */
export default function IRRExplainerModal({ isOpen, onClose, rentGrowthRate = 5, propertyAppreciationRate = 6, managementFeePercent = 0 }) {
  const narrow = useIsNarrow();
  if (!isOpen) return null;

  const h3 = { fontSize: "15px", fontWeight: "700", color: "#333", margin: "22px 0 8px" };
  const p = { fontSize: "14px", lineHeight: 1.65, color: "#444", margin: "0 0 10px" };
  const th = { textAlign: "left", padding: "7px 10px", borderBottom: "2px solid #e0e0e0",
               fontSize: "13px", color: "#555" };
  const td = { padding: "7px 10px", borderBottom: "1px solid #eee", fontSize: "13px" };
  const num = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

  return (
    <div
      style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1100,
               display: "flex", alignItems: "center", justifyContent: "center",
               padding: narrow ? "12px" : "24px" }}
      onClick={onClose}
    >
      <div
        style={{ backgroundColor: "#fff", borderRadius: "10px", maxWidth: "720px", width: "100%",
                 maxHeight: "88vh", overflowY: "auto", padding: narrow ? "20px 16px" : "28px 32px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      borderBottom: "2px solid #000", paddingBottom: "10px", marginBottom: "4px" }}>
          <h2 style={{ margin: 0, fontSize: narrow ? "18px" : "21px" }}>How the IRR is calculated</h2>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: "none", border: "none", fontSize: "26px", lineHeight: 1,
                           cursor: "pointer", color: "#666" }}>×</button>
        </div>

        <h3 style={h3}>The short version</h3>
        <p style={p}>
          IRR is the annual return that makes this deal break even. You put money in once, take
          money out every year, and sell in year 10. The IRR is the single interest rate at
          which all of that cancels out — so <strong>higher is better</strong>, and it is
          directly comparable to any other investment quoted as a yearly percentage.
        </p>
        <p style={p}>
          It differs from cap rate in one important way: cap rate looks at a single year and
          ignores the mortgage. IRR covers the whole ten years and counts the loan, the rent
          growth and the eventual sale.
        </p>

        <h3 style={h3}>What goes in, what comes out</h3>
        <p style={p}>
          <strong>Money out, once:</strong> your Total Out of Pocket — the down payment, the
          closing costs, any renovation budget, and the carrying costs for the months the
          renovation takes, when the apartment is paying nothing. Not the purchase price: the
          part of the price covered by the mortgage is not your cash.
        </p>
        <p style={p}>
          <strong>Money in, each year:</strong> rent, minus taxes and common charges, minus the
          mortgage payment. It can be negative. The rent grows {rentGrowthRate}% a year and
          compounds; the taxes, common charges and interest are held flat for the whole hold,
          which flatters the later years. The Rental Value Analysis shows year 1 of this, per
          month, where the report shows every year, per year.
        </p>
        {managementFeePercent > 0 && (
          <p style={p}>
            <strong>Management:</strong> from year 2, {managementFeePercent}% of the rent
            collected comes off that cash flow. Year 1 is the lease-up and is not charged. You
            can switch it off, or change the percentage, in IRR Assumptions.
          </p>
        )}
        <p style={p}>
          <strong>Money in, year 10:</strong> the sale. The property is assumed to have
          appreciated {propertyAppreciationRate}% a year; selling costs of 6.825% come off, then
          the outstanding mortgage is repaid. What is left is added to that final year.
        </p>
        <p style={p}>
          The financing is <strong>interest-only</strong>, so nothing is repaid along the way
          and the loan at the sale is the same size as the loan on day one. Every dollar of
          principal comes back out of the sale, which is why the final year dwarfs the other
          nine.
        </p>

        <h3 style={h3}>A simple example</h3>
        <p style={p}>
          A $1,000,000 apartment, 25% down, interest-only at 5.75%. You bring $250,000 of
          deposit plus about $35,000 of closing costs &mdash; <strong>$285,000 out of
          pocket</strong>. It rents for $5,500 a month against $2,100 of taxes and common
          charges and $3,594 of interest, so it starts at a small loss. Rent then grows 5% a
          year while those costs stay put. (This example is fixed at 5% growth and 6%
          appreciation whatever the two inputs at the top of the report are set to, so the
          figures below always mean the same thing.)
          {managementFeePercent > 0 && " The figures below are before any management fee."}
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: "10px" }}>
            <thead>
              <tr>
                <th style={th}>Year</th>
                <th style={{ ...th, textAlign: "right" }}>Cash flow</th>
                <th style={{ ...th, textAlign: "right" }}>Property value</th>
                <th style={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={td}>0</td><td style={num}>&minus;$285,000</td><td style={num}>$1,000,000</td><td style={td}>what you put in</td></tr>
              <tr><td style={td}>1</td><td style={num}>&minus;$2,325</td><td style={num}>$1,060,000</td><td style={td}>rent does not quite cover costs</td></tr>
              <tr><td style={td}>2</td><td style={num}>+$975</td><td style={num}>$1,123,600</td><td style={td}>the first year it pays for itself</td></tr>
              <tr><td style={td}>5</td><td style={num}>+$11,898</td><td style={num}>$1,338,226</td><td style={td}>rent is now $6,685 against unchanged costs</td></tr>
              <tr><td style={td}>10</td><td style={num}>+$952,685</td><td style={num}>$1,790,848</td><td style={td}>$34,063 of rent plus the sale, less 6.825% costs and the $750,000 loan</td></tr>
            </tbody>
          </table>
        </div>
        <p style={p}>
          $285,000 in, one lean year, nine paying ones, and about $919,000 of equity back at
          the end. The rate that balances those is <strong>14.7%</strong> &mdash; that is the
          IRR.
        </p>
        <p style={p}>
          The shape is worth noticing. The deal is under water only in year 1; by year 2 the
          rent has caught the costs, and by year 10 it throws off $34,000 a year. Over the
          hold that is about <strong>$147,000 of rent</strong> against a $919,000 exit &mdash;
          so the sale still dominates the return, but the rent is no longer a rounding error
          in it.
        </p>

        <h3 style={h3}>What moves the number</h3>
        <p style={p}>
          Each row below is that one change applied to the example above, which starts at
          <strong> 14.7%</strong>. The sizes are as telling as the directions.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={th}>Change</th>
                <th style={{ ...th, textAlign: "right" }}>IRR</th>
                <th style={th}>Why</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={td}>Appreciation 6% &rarr; 4%</td><td style={num}><strong>&minus;3.6 pts</strong></td><td style={td}>The largest single influence, and it is an assumption rather than a measurement.</td></tr>
              <tr><td style={td}>Appreciation 6% &rarr; 8%</td><td style={num}><strong>+3.2 pts</strong></td><td style={td}>Same lever the other way. Most of the return is the year-10 sale.</td></tr>
              <tr><td style={td}>Down payment 25% &rarr; 15%</td><td style={num}>+2.5 pts</td><td style={td}>Less of your own cash against the same gain. Leverage.</td></tr>
              <tr><td style={td}>Mortgage rate 5.75% &rarr; 7.25%</td><td style={num}>&minus;2.4 pts</td><td style={td}>Interest comes off every year&rsquo;s cash flow.</td></tr>
              <tr><td style={td}>Rent growth 5% &rarr; 8%</td><td style={num}>+1.9 pts</td><td style={td}>Compounds for ten years over costs that do not move.</td></tr>
              <tr><td style={td}>Rent growth 5% &rarr; 2%</td><td style={num}>&minus;1.7 pts</td><td style={td}>The same lever down. Rent growth is the second assumption worth arguing about.</td></tr>
              <tr><td style={td}>Rent +$500/month</td><td style={num}>+1.5 pts</td><td style={td}>A better starting figure, which the growth then compounds.</td></tr>
              <tr><td style={td}>Purchase price &minus;10%</td><td style={num}>+1.1 pts</td><td style={td}>Cheaper entry, smaller deposit, same future value.</td></tr>
              <tr><td style={td}>Renovation +$25,000</td><td style={num}>&minus;1.0 pts</td><td style={td}>Raises the cash-in figure everything is measured against.</td></tr>
              <tr><td style={td}>Taxes or charges +$300/month</td><td style={num}>&minus;0.8 pts</td><td style={td}>Reduces cash flow for ten years running.</td></tr>
            </tbody>
          </table>
        </div>
        <p style={p}>
          The two rates at the top of the report &mdash; appreciation and rent growth &mdash;
          are between them worth more than five points, and neither is a measurement. They are
          the numbers to argue about before anything else on this page.
        </p>

        <h3 style={h3}>Worth knowing</h3>
        <p style={p}>
          The appreciation and rent growth rates are <strong>assumptions, not forecasts</strong>.
          They are the two inputs you can change at the top of the report, and they are the
          two largest levers on it. Appreciation is still much the bigger of the two, because
          the sale dominates the result: a point on it moves the IRR by about 1.7 points,
          against roughly 0.6 for a point of rent growth. But 0.6 is a real number — rent
          growth is a third of the appreciation rate's influence, not the rounding error it
          looks like.
        </p>
        <p style={p}>
          The projection also assumes the property is held for the full ten years and sold at the
          end, that it is never vacant, and that rent is collected every month.
          {managementFeePercent > 0 && ` The ${managementFeePercent}% management fee is charged on
            that full year's rent from year 2 on.`}
        </p>
      </div>
    </div>
  );
}
